import { useId } from '@sequencehq/utils'
import {
  Text,
  BorderRadius8,
  GreyGrey40,
  GreyGrey80,
  GreyWhite,
  Primary14SemiBold,
  SettingsGroup,
  ShadowXs,
  Spinner,
  Center,
  Flex
} from '@sequencehq/core-components'
import { FC, ReactNode } from 'react'
import { LabsActivationPulse } from './LabsActivationPulse'
import { LabsBorderBeam } from './LabsBorderBeam'

/**
 * The "Sequence Labs" settings card plus its one-shot activation flourish: the
 * border beam wraps the card and the shock-wave pulse plays inside it. Everything
 * the effect needs is bundled here (rather than threaded through the shared
 * SettingsGroup) so the whole experiment can be removed by deleting the labs
 * folder and swapping this back for a plain SettingsGroup.
 *
 * `pulseKey` is the replay trigger: bump it (keeping it > 0) to remount and
 * replay both animations.
 */
export const LabsActivationCard: FC<{
  title: string
  footer: string
  isLoading?: boolean
  pulseKey: number
  children: ReactNode
}> = ({ title, footer, isLoading, pulseKey, children }) => {
  const sectionId = useId(undefined, 'labs-activation')
  const isActive = pulseKey > 0

  return (
    <Flex
      as="section"
      width="full"
      maxWidth="600px"
      flexDirection="column"
      aria-labelledby={sectionId}
    >
      <Text
        as="h3"
        marginBottom="16px"
        id={sectionId}
        {...Primary14SemiBold}
        color={GreyGrey80}
      >
        {title}
      </Text>

      <LabsBorderBeam
        key={pulseKey}
        active={isActive}
        data-testid="labs.activationBeam"
      >
        <Flex
          width="100%"
          flexDirection="column"
          justifyContent="center"
          borderRadius={BorderRadius8}
          border={`1px solid ${GreyGrey40}`}
          boxShadow={ShadowXs}
          backgroundColor={GreyWhite}
          position="relative"
        >
          {/* Pulse sits over the card background but behind the rows, which are
              made transparent (below) so it shows through. */}
          {isActive && <LabsActivationPulse key={pulseKey} />}
          <Flex
            px="16px"
            flexDirection="column"
            justifyContent="center"
            width="100%"
            position="relative"
            zIndex={1}
            // The rows are SettingsFormControls, which paint themselves white.
            // Override them to transparent from here so the pulse shows through,
            // keeping the transparency scoped to this deletable labs card rather
            // than adding a prop to the shared SettingsFormControl. `& > div`
            // (specificity 0,1,1) reliably beats the row's own bg class (0,1,0).
            css={{ '& > div': { backgroundColor: 'transparent' } }}
          >
            {isLoading ? (
              <Center height="64px">
                <Spinner />
              </Center>
            ) : (
              children
            )}
          </Flex>
        </Flex>
      </LabsBorderBeam>

      <SettingsGroup.Footer>{footer}</SettingsGroup.Footer>
    </Flex>
  )
}
